<template>
  <div class="h-[calc(100vh-178px)] flex gap-x-4" v-if="loaded">
    <ContentBlock class="basis-60" title="Filters">
      <div class="p-4 flex flex-col gap-y-4">
        <SingleSelect v-if="!linksMode" label="Year" v-model="proxyFilter.year" :options="years" placeholder="Select Year"
          inline />
        <SingleSelect label="Day of Week" v-model="proxyFilter.dayOfWeek" :options="daysOfWeek"
          placeholder="Select Day of Week" inline />
        <SingleObjectSelect label="Hour of Day" v-model="proxyFilter.hour" :options="hours"
          placeholder="Select Hour of Day" inline />
        <SingleSelect v-if="!linksMode" label="Vehicle Type" v-model="proxyFilter.vehicleType" :options="vehicleTypes"
          placeholder="Select Vehicle Type" inline />
        <RangeInput label="Speed" unit=" mph" :max="maxSpeed" :step="speedStep" v-model:min-value="proxyFilter.minSpeed"
          v-model:max-value="proxyFilter.maxSpeed" />
        <RangeInput :label="volumeLabel" :unit="volumeUnit" :max="maxVolume" :step="volumeStep"
          v-model:min-value="proxyFilter.minVolume" v-model:max-value="proxyFilter.maxVolume" />
        <div
          class="mt-8 flex text-zinc-50 space-x-2 items-center justify-center px-3 py-2 rounded-lg border border-accent bg-accent/75 hover:bg-accent text-sm cursor-pointer"
          @click="applyFilter">
          <span>Apply</span>
        </div>
      </div>
    </ContentBlock>
    <div class="grow rounded-lg overflow-hidden h-full relative" ref="mapElement">
      <div v-if="processing"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-3 bg-zinc-900/60 text-zinc-50">
        <IconSpinner class="animate-spin w-10 h-10" />
        <span class="text-sm">{{ processingMessage }}</span>
      </div>
      <div v-if="focusedLink"
        class="bg-zinc-100 dark:bg-zinc-800 px-4 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-md absolute right-4 top-4 w-64 text-sm">
        <div class="flex items-center gap-x-2 mb-1">
          <span class="inline-block h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: focusedLink.color }"></span>
          <span class="font-semibold">Link {{ focusedLink.id }}</span>
          <button v-if="focusedLinkPinned" type="button"
            class="ml-auto text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 size-4" aria-label="Close"
            @click="clearFocusedLink">
            <IconClose class="size-4" />
          </button>
        </div>
        <div v-for="[label, value] in focusedLink.rows" :key="label" class="flex justify-between gap-x-3">
          <div class="text-zinc-500 dark:text-zinc-400">{{ label }}</div>
          <div class="text-right">{{ value }}</div>
        </div>
      </div>
      <div v-if="linksMode" class="absolute bottom-4 right-4 w-56 bg-zinc-100 dark:bg-zinc-800 rounded-md shadow">
        <div class="rounded-t-md bg-zinc-200 dark:bg-zinc-700 p-2 flex justify-between items-center">
          <h5 class="font-medium text-sm">Legend</h5>
        </div>
        <div class="p-2 text-sm">
          <div class="text-xs text-zinc-500 dark:text-zinc-400 mb-1">Speed vs. free-flow speed</div>
          <ul>
            <li v-for="item in legend" :key="item.label" class="flex items-center justify-between py-0.5">
              <div>{{ item.label }}</div>
              <span class="inline-block w-9 h-1.5 rounded" :style="{ backgroundColor: item.color }"></span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <div class="h-[calc(100vh-178px)] flex place-items-center justify-center" v-else>
    <IconSpinner class="animate-spin w-12 h-12" />
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, nextTick } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuth0 } from "@auth0/auth0-vue";
import { first, last, trimStart, isEmpty, isPlainObject, isString, toLower, uniq, cloneDeep, get } from "lodash";
import { bbox } from "@turf/turf";
import AirSageMap from "@/lib/map";
import {
  buildSpeedVolumeGeoJSON,
  DAILY_KEY,
  DAY_PROPERTY,
  LINK_ID_PROPERTY,
  SPEED_LIMIT_PROPERTY,
  speedProperty,
  volumeProperty
} from "@/lib/speedVolume/links";
import { StudiesService } from "@/services";
import ContentBlock from "@/components/ContentBlock.vue";
import SingleSelect from "@/components/SingleSelect.vue";
import SingleObjectSelect from "@/components/SingleObjectSelect.vue";
import RangeInput from "@/components/RangeInput.vue";
import IconSpinner from "@/components/icons/IconSpinner.vue";
import IconClose from "@/components/icons/IconClose.vue";
import { useStudyStore } from "@/stores/study";
import { useAlertStore } from "@/stores/alert";

const studyStore = useStudyStore();
const alertStore = useAlertStore();
const route = useRoute();
const { getAccessTokenSilently } = useAuth0();
const { config } = storeToRefs(studyStore);

const SPEED_COLORS = { slow: "#AA3F39", moderate: "#AA9839", free: "#2C8437" };
const legend = [
  { label: "< 40%", color: SPEED_COLORS.slow },
  { label: "40% - 80%", color: SPEED_COLORS.moderate },
  { label: "> 80%", color: SPEED_COLORS.free }
];

// legacy vector tile source, used when no browser-side inputs are published for the study
const LINE_LAYER_ID = "lines";
// browser-side links source/layers (GeoJSON joined in the browser, or a pre-built vector tileset)
const LINKS_SOURCE_ID = "sv-links";
const LINKS_LAYER_ID = "sv-links-line";
const LINKS_HIGHLIGHT_LAYER_ID = "sv-links-highlight";
const LINKS_HIGHLIGHT_OUTLINE_LAYER_ID = "sv-links-highlight-outline";
// the highlight is a white ring edged in dark so it stands out on both the light and dark basemaps
const HIGHLIGHT_PADDING = 4;
const HIGHLIGHT_OUTLINE_PADDING = HIGHLIGHT_PADDING + 3;
const HIGHLIGHT_LAYERS = [
  { id: LINKS_HIGHLIGHT_OUTLINE_LAYER_ID, color: "rgba(24, 24, 27, 0.85)", padding: HIGHLIGHT_OUTLINE_PADDING },
  { id: LINKS_HIGHLIGHT_LAYER_ID, color: "rgba(255, 255, 255, 0.95)", padding: HIGHLIGHT_PADDING }
];
const NO_LINK_ID = "";

const loaded = ref(false);
// decided once the inputs request settles: "links-tiles" (pre-built tileset), "links" (browser-side join) or "tiles" (legacy)
const mode = ref(null);
const linksMode = computed(() => ["links", "links-tiles"].includes(mode.value));
// source layer of the links tileset; null when the links are a GeoJSON source
let linksSourceLayer = null;
const processing = ref(false);
const processingMessage = ref("");
const stats = ref(null);
const focusedLink = ref(null);
const focusedLinkPinned = ref(false);
const hoveredLinkId = ref(null);
const highlightedLinkId = computed(() => {
  if (hoveredLinkId.value !== null) {
    return hoveredLinkId.value;
  }
  return focusedLinkPinned.value && focusedLink.value ? focusedLink.value.id : null;
});

const WEEKDAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
// legacy tile studies list the years to switch between; schema configs store date intervals instead
const years = computed(() => (config.value.dates || []).filter(isString));
const vehicleTypes = computed(() => config.value.vehicleTypes || []);
/**
 * Day buckets can be stored as a list of labels (legacy tile studies) or, per the study
 * config schema, a weekday -> bucket label map (listed Monday first).
 */
const daysOfWeek = computed(() => {
  const buckets = config.value.weekdayBuckets || [];
  const labels = isPlainObject(buckets) ? uniq(WEEKDAY_ORDER.map((day) => buckets[day])) : buckets;
  return labels.filter((label) => !isEmpty(label) && toLower(label) !== "omit");
});
const hours = computed(() => {
  const options = Object.entries(config.value.hourBuckets || {}).map(([key, value]) => ({
    name: value,
    value: trimStart(key, "H")
  }));
  if (linksMode.value) {
    options.push({ name: "Daily", value: DAILY_KEY });
  }
  return options;
});
const hourLabel = (hour) => get(hours.value.find((h) => h.value === hour), "name", hour);

const maxSpeed = ref(125);
const speedStep = ref(1);
const maxVolume = ref(250);
const volumeStep = ref(1);
// legacy tiles store volumes in vehicles while the slider is in thousands
const volumeScale = computed(() => (linksMode.value ? 1 : 1000));
const volumeUnit = computed(() => (linksMode.value ? "" : "k"));
const proxyFilter = ref({
  year: "",
  dayOfWeek: "",
  hour: "",
  vehicleType: "",
  minSpeed: 0,
  maxSpeed: 125,
  minVolume: 0,
  maxVolume: 250
});
const filter = ref(cloneDeep(proxyFilter.value));
const volumeLabel = computed(() => (linksMode.value && proxyFilter.value.hour === DAILY_KEY ? "Daily Volume" : "Volume"));

let map = null;
let inputsPromise = null;
let volumeBounds = { hourly: { max: 250, step: 1 }, daily: { max: 250, step: 1 } };

/**
 * Fetch the study's browser-side inputs (proxied by the API). A pre-built links tileset
 * manifest (see the API's scripts/sv-build-links-tiles.js) is preferred; otherwise the
 * roadway GeoJSON and links CSV are joined in the browser. Resolves to null when the
 * study has neither, so the view can fall back to the legacy vector tiles.
 */
async function loadInputs(id) {
  const token = await getAccessTokenSilently();
  const options = (responseType) => ({
    headers: { Authorization: `Bearer ${token}` },
    responseType
  });
  const notFound = (error) => [400, 404].includes(get(error, "response.status"));
  try {
    const manifest = await StudiesService.getInput(id, "tiles", options("json"));
    if (manifest && (manifest.path || manifest.tiles)) {
      return { manifest };
    }
  } catch (error) {
    if (!notFound(error)) {
      throw error;
    }
  }
  try {
    const [roads, csvText] = await Promise.all([
      StudiesService.getInput(id, "roads", options("json")),
      StudiesService.getInput(id, "links", options("text"))
    ]);
    return { roads, csvText };
  } catch (error) {
    if (notFound(error)) {
      return null;
    }
    throw error;
  }
}

watch(config, async () => {
  if (!isEmpty(config.value)) {
    const { id } = route.params;
    // start downloading while the map initializes
    inputsPromise = loadInputs(id).catch((error) => {
      alertStore.alertError(error);
      return null;
    });
    loaded.value = true;
    await nextTick();
    proxyFilter.value.year = last(years.value);
    proxyFilter.value.dayOfWeek = first(daysOfWeek.value);
    proxyFilter.value.hour = "12";
    proxyFilter.value.vehicleType = first(vehicleTypes.value);
    applyFilter();
    loadMap();
  }
});

onBeforeUnmount(studyStore.reset);

function applyFilter() {
  const proxy = cloneDeep(proxyFilter.value);
  filter.value = {
    ...proxy,
    dayOfWeek: toLower(proxy.dayOfWeek),
    vehicleType: toLower(proxy.vehicleType)
  };
}

// the volume slider spans a different range for hourly and daily volumes
watch(() => proxyFilter.value.hour, (hour, previous) => {
  if (!linksMode.value || (hour === DAILY_KEY) === (previous === DAILY_KEY)) {
    return;
  }
  setVolumeBounds(hour === DAILY_KEY ? volumeBounds.daily : volumeBounds.hourly);
});

function setVolumeBounds({ max, step }) {
  maxVolume.value = max;
  volumeStep.value = step;
  proxyFilter.value.minVolume = 0;
  proxyFilter.value.maxVolume = max;
}

function roundUp(value, step) {
  return Math.max(step, Math.ceil(value / step) * step);
}

/** Slider step that keeps the range to a few hundred stops. */
function niceStep(max) {
  return [1, 5, 10, 50, 100, 500, 1000, 5000, 10000].find((step) => max / step <= 250) || 10000;
}

// ---------------------------------------------------------------------------
// legacy vector tile mode
// ---------------------------------------------------------------------------

const baseFilter = ["all", ["has", "speed"], ["has", "volume"], [">", ["to-number", ["get", "speed"]], 0]];

function tileLayerName(vehicle, day, year) {
  return `${LINE_LAYER_ID}-${toLower(vehicle)}-${toLower(day)}-${year}`;
}

function tilePaint(hour) {
  return {
    "line-color": [
      "step",
      ["/", ["to-number", ["get", `sh${hour}`], 0], ["to-number", ["get", "AprxSpeedL"]]],
      SPEED_COLORS.slow,
      0.40,
      SPEED_COLORS.moderate,
      0.80,
      SPEED_COLORS.free
    ],
    "line-width": [
      "step",
      ["to-number", ["get", `vh${hour}`], 0],
      1,
      2500,
      2,
      7500,
      3,
      10000,
      6,
      15000,
      8,
      20000,
      10,
      25000,
      12,
      30000,
      14,
      35000,
      16
    ]
  };
}

function applyTileFilter() {
  years.value.forEach((year) => {
    daysOfWeek.value.forEach((day) => {
      vehicleTypes.value.forEach((vehicle) => {
        const layer = tileLayerName(vehicle, day, year);
        if (map.getLayer(layer)) {
          map.setLayoutProperty(layer, "visibility", "none");
        }
      });
    });
  });
  const layer = tileLayerName(filter.value.vehicleType, filter.value.dayOfWeek, filter.value.year);
  if (map.getLayer(layer)) {
    const speed = ["to-number", ["get", `sh${filter.value.hour}`], 0];
    const volume = ["to-number", ["get", `vh${filter.value.hour}`], 0];
    const layerFilter = [
      "all",
      baseFilter,
      [">=", speed, filter.value.minSpeed],
      ["<=", speed, filter.value.maxSpeed],
      [">=", volume, filter.value.minVolume * volumeScale.value],
      ["<=", volume, filter.value.maxVolume * volumeScale.value]
    ];
    map.setFilter(layer, layerFilter);
    map.setLayoutProperty(layer, "visibility", "visible");
    Object.entries(tilePaint(filter.value.hour)).forEach(([key, value]) => map.setPaintProperty(layer, key, value));
  }
}

function addTileLayers() {
  const { id } = route.params;
  map.addSource(LINE_LAYER_ID, {
    type: "vector",
    tiles: [`${import.meta.env.VITE_TILE_CDN_URL}/${id}/{z}/{x}/{y}.pbf`],
    promoteId: "link_id",
    minzoom: 0,
    maxzoom: 11
  });

  years.value.forEach((year) => {
    daysOfWeek.value.forEach((day) => {
      vehicleTypes.value.forEach((vehicle) => {
        const sourceLayer = `${toLower(vehicle)}-${toLower(day)}-${year}`;
        const layer = tileLayerName(vehicle, day, year);
        map.addLayer({
          id: layer,
          type: "line",
          source: LINE_LAYER_ID,
          "source-layer": sourceLayer,
          paint: {
            "line-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 1, 0.75],
            ...tilePaint(filter.value.hour)
          }
        });

        map.setFilter(layer, baseFilter);
        map.setLayoutProperty(layer, "visibility", "none");
      });
    });
  });
  map.setLayoutProperty(tileLayerName(filter.value.vehicleType, filter.value.dayOfWeek, filter.value.year), "visibility", "visible");
  map.centerOn(config.value.boundBox, { easing: () => 1 });
}

// ---------------------------------------------------------------------------
// browser-side links mode
// ---------------------------------------------------------------------------

const speedExpression = (hour) => ["to-number", ["get", speedProperty(hour)], 0];
const volumeExpression = (hour) => ["to-number", ["get", volumeProperty(hour)], 0];
const limitExpression = ["to-number", ["get", SPEED_LIMIT_PROPERTY], 0];

/** Links with a speed, a free-flow speed and volume for the hour are drawn with data colors. */
function hasDataExpression(hour) {
  return ["all", [">", speedExpression(hour), 0], [">", limitExpression, 0], [">", volumeExpression(hour), 0]];
}

/**
 * Line width scaled by volume, thinner when zoomed out so the dense network stays legible.
 * "zoom" must be the top-level interpolate input, so any casing padding is added inside it.
 */
function linksWidth(hour, padding = 0) {
  const scale = hour === DAILY_KEY ? stats.value.dailyVolume.p95 : stats.value.hourlyVolume.p95;
  const width = ["interpolate", ["linear"], volumeExpression(hour), 0, 1.5, Math.max(scale, 1), 10];
  return ["interpolate", ["linear"], ["zoom"], 8, ["+", ["*", width, 0.5], padding], 12, ["+", width, padding]];
}

function linksPaint(hour) {
  return {
    "line-color": ["step", ["/", speedExpression(hour), limitExpression], SPEED_COLORS.slow, 0.40, SPEED_COLORS.moderate, 0.80, SPEED_COLORS.free],
    "line-width": linksWidth(hour),
    "line-opacity": 0.9
  };
}

/**
 * Only links with a speed, free-flow speed and volume for the hour are shown. Sliders at
 * their full extent impose no upper bound so outliers beyond the slider range stay visible.
 */
function linksFilter() {
  const { hour, dayOfWeek, minSpeed, maxSpeed: maxSpeedValue, minVolume, maxVolume: maxVolumeValue } = filter.value;
  const parts = [
    "all",
    ["==", ["get", DAY_PROPERTY], dayOfWeek],
    hasDataExpression(hour),
    [">=", speedExpression(hour), minSpeed],
    [">=", volumeExpression(hour), minVolume * volumeScale.value]
  ];
  if (maxSpeedValue < maxSpeed.value) {
    parts.push(["<=", speedExpression(hour), maxSpeedValue]);
  }
  if (maxVolumeValue < maxVolume.value) {
    parts.push(["<=", volumeExpression(hour), maxVolumeValue * volumeScale.value]);
  }
  return parts;
}

function linksHighlightFilter() {
  return ["all", linksFilter(), ["==", ["get", LINK_ID_PROPERTY], highlightedLinkId.value ?? NO_LINK_ID]];
}

function applyLinksFilter() {
  if (!map.getLayer(LINKS_LAYER_ID)) {
    return;
  }
  map.setFilter(LINKS_LAYER_ID, linksFilter());
  Object.entries(linksPaint(filter.value.hour)).forEach(([key, value]) => map.setPaintProperty(LINKS_LAYER_ID, key, value));
  HIGHLIGHT_LAYERS.forEach(({ id, padding }) => {
    map.setPaintProperty(id, "line-width", linksWidth(filter.value.hour, padding));
    map.setFilter(id, linksHighlightFilter());
  });
  if (focusedLink.value) {
    refreshFocusedLink();
  }
}

watch(highlightedLinkId, () => {
  if (map && map.getLayer(LINKS_HIGHLIGHT_LAYER_ID)) {
    HIGHLIGHT_LAYERS.forEach(({ id }) => map.setFilter(id, linksHighlightFilter()));
  }
});

function initializeLinksFilters(linkStats) {
  // a handful of model links report implausible speeds; bound the slider near the 99th percentile instead of the max
  maxSpeed.value = roundUp(Math.min(linkStats.speed.max, Math.max(linkStats.speed.p99 * 1.25, 60)), 5);
  speedStep.value = 1;
  const hourlyMax = roundUp(linkStats.hourlyVolume.max, niceStep(linkStats.hourlyVolume.max));
  const dailyMax = roundUp(linkStats.dailyVolume.max, niceStep(linkStats.dailyVolume.max));
  volumeBounds = {
    hourly: { max: hourlyMax, step: niceStep(hourlyMax) },
    daily: { max: dailyMax, step: niceStep(dailyMax) }
  };
  proxyFilter.value.minSpeed = 0;
  proxyFilter.value.maxSpeed = maxSpeed.value;
  setVolumeBounds(proxyFilter.value.hour === DAILY_KEY ? volumeBounds.daily : volumeBounds.hourly);
  applyFilter();
}

const formatNumber = (value, digits = 0, suffix = "") => (Number.isFinite(value) ? `${value.toLocaleString(undefined, { maximumFractionDigits: digits })}${suffix}` : "—");

function describeLink(props) {
  const { hour } = filter.value;
  const speed = props[speedProperty(hour)];
  const volume = props[volumeProperty(hour)];
  const limit = props[SPEED_LIMIT_PROPERTY];
  const ratio = speed > 0 && limit > 0 ? speed / limit : null;
  // a pinned link can lack data for a newly selected hour; show a neutral dot in that case
  let color = "#71717A";
  if (ratio !== null && volume > 0) {
    color = ratio < 0.4 ? SPEED_COLORS.slow : ratio < 0.8 ? SPEED_COLORS.moderate : SPEED_COLORS.free;
  }
  const rows = [
    ["Day", daysOfWeek.value.find((d) => toLower(d) === props[DAY_PROPERTY]) || props[DAY_PROPERTY]],
    [hour === DAILY_KEY ? "Period" : "Hour", hourLabel(hour)],
    [hour === DAILY_KEY ? "Avg Speed" : "Speed", formatNumber(speed, 1, " mph")],
    ["Free-flow Speed", formatNumber(limit, 0, " mph")],
    ["% of Free-flow", ratio !== null ? formatNumber(ratio * 100, 0, "%") : "—"],
    [hour === DAILY_KEY ? "Daily Volume" : "Volume", formatNumber(volume)]
  ];
  if (hour !== DAILY_KEY) {
    rows.push(["Daily Volume", formatNumber(props[volumeProperty(DAILY_KEY)])]);
  }
  return { id: props[LINK_ID_PROPERTY], color, rows, props };
}

function focusLink(feature, pin) {
  if (focusedLinkPinned.value && !pin) {
    return;
  }
  focusedLink.value = describeLink(feature.properties);
  if (pin) {
    focusedLinkPinned.value = true;
  }
}

/** Re-describe the focused link after the filter changes so the card reflects the new hour/day. */
function refreshFocusedLink() {
  const features = map.querySourceFeatures(LINKS_SOURCE_ID, {
    ...(linksSourceLayer ? { sourceLayer: linksSourceLayer } : {}),
    filter: ["all", ["==", ["get", LINK_ID_PROPERTY], focusedLink.value.id], ["==", ["get", DAY_PROPERTY], filter.value.dayOfWeek]]
  });
  if (features.length) {
    focusedLink.value = describeLink(features[0].properties);
  } else {
    focusedLink.value = describeLink({ ...focusedLink.value.props, [DAY_PROPERTY]: filter.value.dayOfWeek });
  }
}

function clearFocusedLink() {
  focusedLink.value = null;
  focusedLinkPinned.value = false;
}

/**
 * Add the links source and layers. `source` is either a GeoJSON source spec (browser-side
 * join) or a vector source spec plus `sourceLayer` (pre-built tileset); the filter and paint
 * expressions are identical because both carry the same feature properties.
 */
function addLinksLayers(source, sourceLayer = null) {
  linksSourceLayer = sourceLayer;
  const layerSource = sourceLayer ? { source: LINKS_SOURCE_ID, "source-layer": sourceLayer } : { source: LINKS_SOURCE_ID };
  map.addSource(LINKS_SOURCE_ID, source);

  HIGHLIGHT_LAYERS.forEach(({ id, color, padding }) => {
    map.addLayer({
      id,
      type: "line",
      ...layerSource,
      filter: linksHighlightFilter(),
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": color,
        "line-width": linksWidth(filter.value.hour, padding)
      }
    });
  });
  map.addLayer({
    id: LINKS_LAYER_ID,
    type: "line",
    ...layerSource,
    filter: linksFilter(),
    layout: { "line-cap": "round", "line-join": "round" },
    paint: linksPaint(filter.value.hour)
  });

  const featureAt = (point) => map.queryRenderedFeatures(point, { layers: [LINKS_LAYER_ID] })[0] || null;

  // hit-testing a dense network on every mouse move is costly; coalesce to one query per frame
  let hoverFrame = null;
  map.on("mousemove", (e) => {
    if (hoverFrame !== null) {
      return;
    }
    hoverFrame = requestAnimationFrame(() => {
      hoverFrame = null;
      const feature = featureAt(e.point);
      map.getCanvas().style.cursor = feature ? "pointer" : "";
      hoveredLinkId.value = feature ? feature.properties[LINK_ID_PROPERTY] : null;
      if (feature) {
        focusLink(feature, false);
      } else if (!focusedLinkPinned.value) {
        focusedLink.value = null;
      }
    });
  });
  map.on("mouseout", () => {
    map.getCanvas().style.cursor = "";
    hoveredLinkId.value = null;
    if (!focusedLinkPinned.value) {
      focusedLink.value = null;
    }
  });
  map.on("click", (e) => {
    const feature = featureAt(e.point);
    if (feature) {
      focusLink(feature, true);
    } else {
      clearFocusedLink();
    }
  });
}

// ---------------------------------------------------------------------------

watch(filter, () => {
  if (!map || !mode.value) {
    return;
  }
  if (linksMode.value) {
    applyLinksFilter();
  } else {
    applyTileFilter();
  }
}, { deep: true });

async function loadLayers() {
  processing.value = true;
  processingMessage.value = "Loading link speeds and volumes…";
  const inputs = await inputsPromise;
  if (!map) {
    return;
  }

  if (inputs && inputs.manifest) {
    mode.value = "links-tiles";
    const { manifest } = inputs;
    console.debug("speed/volume links tileset", manifest);
    stats.value = manifest.stats;
    initializeLinksFilters(manifest.stats);
    addLinksLayers({
      type: "vector",
      // the manifest can carry absolute tile URLs (dev harness); otherwise the path is relative to the tiles CDN
      tiles: manifest.tiles || [`${import.meta.env.VITE_TILE_CDN_URL}/${manifest.path}/{z}/{x}/{y}.pbf`],
      minzoom: manifest.minzoom ?? 0,
      maxzoom: manifest.maxzoom ?? 14,
      ...(manifest.bounds ? { bounds: manifest.bounds } : {})
    }, manifest.sourceLayer || "links");
    if (manifest.bounds) {
      map.centerOn(manifest.bounds, { easing: () => 1 });
    } else if (config.value.boundBox) {
      map.centerOn(config.value.boundBox, { easing: () => 1 });
    }
  } else if (inputs) {
    mode.value = "links";
    processingMessage.value = "Joining links to roadways…";
    // let the overlay paint before blocking on the join
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 0));

    const { geojson, stats: linkStats } = buildSpeedVolumeGeoJSON(inputs.roads, inputs.csvText, { dayBuckets: daysOfWeek.value });
    console.debug("speed/volume links processed", linkStats);
    stats.value = linkStats;
    initializeLinksFilters(linkStats);
    addLinksLayers({ type: "geojson", data: geojson });
    if (inputs.roads.features.length) {
      map.centerOn(bbox(inputs.roads), { easing: () => 1 });
    } else if (config.value.boundBox) {
      map.centerOn(config.value.boundBox, { easing: () => 1 });
    }
  } else {
    mode.value = "tiles";
    addTileLayers();
    applyTileFilter();
  }
  processing.value = false;
}

const mapElement = ref(null);
function loadMap() {
  map = new AirSageMap(mapElement.value, {
    eventHandlers: {
      "style.load": loadLayers
    }
  });
}
</script>
